<template>
  <section class="card" :aria-labelledby="`${idBase}-titulo`">
    <div class="card-body">
      <h2 :id="`${idBase}-titulo`" class="h5">Documentos do ambiente</h2>
      <p class="form-text mt-0">
        Plantas, laudos, alvarás, contratos e fotos do ambiente. PDF, JPG ou PNG de até 10 MB.
      </p>

      <!-- Região viva permanente: criada junto com a mensagem, ela não seria anunciada (WCAG 4.1.3). -->
      <div class="visually-hidden" role="status" aria-live="polite">{{ anuncio }}</div>

      <p v-if="erroGeral" class="alert alert-danger" role="alert">{{ erroGeral }}</p>

      <ConsulteGrid
        :columns="colunas"
        :data-source="dataSource"
        :show-views-manager="false"
        titulo-relatorio="Documentos do ambiente"
        nome-arquivo-relatorio="Documentos_ambiente"
      >
        <template #dataDocumento="{ row }">{{ formatarData(row.dataDocumento) }}</template>
        <template #nomeArquivo="{ row }">
          <a
            v-if="row.url"
            :href="row.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Abrir o arquivo ${row.nomeArquivo} (abre em nova aba)`"
            :title="`Abrir ${row.nomeArquivo} em nova aba`"
          >
            <i class="mdi mdi-paperclip me-1" aria-hidden="true"></i>{{ row.nomeArquivo }}
          </a>
          <span v-else>
            {{ row.nomeArquivo }}
            <small class="d-block text-danger">Arquivo indisponível no armazenamento</small>
          </span>
        </template>
        <template #tamanho="{ row }">{{ formatarTamanho(row.tamanho) }}</template>
        <template #acoes="{ row }">
          <button
            v-if="permissao.excluir"
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="ocupado"
            :aria-label="`Excluir o documento ${row.descricao}`"
            :title="`Excluir o documento ${row.descricao}`"
            @click="excluir(row)"
          >
            <i class="mdi mdi-delete" aria-hidden="true"></i>
            <span class="ms-1">Excluir</span>
          </button>
          <span v-else class="visually-hidden">Sem ações disponíveis</span>
        </template>
      </ConsulteGrid>

      <!-- ===================================================== novo documento (incluir) -->
      <form
        v-if="permissao.incluir"
        class="mt-4"
        novalidate
        :aria-labelledby="`${idBase}-novo`"
        @submit.prevent="enviar"
      >
        <h3 :id="`${idBase}-novo`" class="h6">Anexar documento</h3>

        <div class="row g-3">
          <div class="col-12 col-lg-6">
            <label :for="`${idBase}-descricao`" class="form-label">
              Descrição <span aria-hidden="true">*</span>
            </label>
            <input
              :id="`${idBase}-descricao`"
              v-model="novo.descricao"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': erros.descricao }"
              maxlength="200"
              required
              autocomplete="off"
              :disabled="ocupado"
              :aria-invalid="erros.descricao ? 'true' : undefined"
              :aria-describedby="erros.descricao ? `${idBase}-descricao-erro` : undefined"
            />
            <div v-if="erros.descricao" :id="`${idBase}-descricao-erro`" class="invalid-feedback d-block">
              {{ erros.descricao }}
            </div>
          </div>

          <div class="col-12 col-sm-6 col-lg-3">
            <DropDownConsulte
              :id="`${idBase}-tipo`"
              v-model.number="novo.tipoDocumento"
              :array="tiposDocumento"
              row-label="texto"
              row-value="valor"
              label="Tipo do documento"
              placeholder="Selecione o tipo"
              required
              :disabled="ocupado"
              :mensagem-erro="erros.tipoDocumento"
            />
          </div>

          <div class="col-12 col-sm-6 col-lg-3">
            <label :for="`${idBase}-data`" class="form-label">
              Data de criação <span aria-hidden="true">*</span>
            </label>
            <input
              :id="`${idBase}-data`"
              v-model="novo.dataDocumento"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': erros.dataDocumento }"
              :max="hoje"
              required
              :disabled="ocupado"
              :aria-invalid="erros.dataDocumento ? 'true' : undefined"
              :aria-describedby="erros.dataDocumento ? `${idBase}-data-erro` : undefined"
            />
            <div v-if="erros.dataDocumento" :id="`${idBase}-data-erro`" class="invalid-feedback d-block">
              {{ erros.dataDocumento }}
            </div>
          </div>

          <div class="col-12 col-lg-9">
            <label :for="`${idBase}-arquivo`" class="form-label">
              Arquivo (PDF, JPG ou PNG, até 10 MB) <span aria-hidden="true">*</span>
            </label>
            <input
              :id="`${idBase}-arquivo`"
              ref="campoArquivo"
              type="file"
              class="form-control"
              :class="{ 'is-invalid': erros.arquivo }"
              accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
              required
              :disabled="ocupado"
              :aria-invalid="erros.arquivo ? 'true' : undefined"
              :aria-describedby="erros.arquivo ? `${idBase}-arquivo-erro` : undefined"
              @change="selecionarArquivo"
            />
            <div v-if="erros.arquivo" :id="`${idBase}-arquivo-erro`" class="invalid-feedback d-block">
              {{ erros.arquivo }}
            </div>
          </div>

          <div class="col-12 col-lg-3 d-flex align-items-end">
            <button type="submit" class="btn btn-primary w-100" :disabled="ocupado">
              <span v-if="ocupado" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
              <i v-else class="mdi mdi-paperclip me-1" aria-hidden="true"></i>
              Anexar documento
            </button>
          </div>
        </div>
      </form>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import ConsulteGrid from '@/components/grid/ConsulteGrid.vue';
  import DropDownConsulte from '@/components/dropdownconsulte/DropDownConsulte.vue';
  import { ConsulteGridColumnsModel } from '@/datasources/consulte-grid';
  import { DataSourceLocal } from '@/datasources/local.datasource';
  import {
    permissaoTelaLiberada,
    PERMISSAO_TELA_INJECT_KEY,
    PermissaoTela,
  } from '@/composables/usePermissaoTela/usePermissaoTela';
  import salaService from '@/services/gestao/sala/sala.service';
  import dialogoService from '@/services/globals/dialogo.service';
  import toastService from '@/services/globals/toast.service';
  import { AnexoSalaModel } from '@/models/gestao/sala/sala.model';
  import { novoId } from '@/uteis/id-unico';

  const TAMANHO_MAXIMO = 10 * 1024 * 1024;
  const TIPOS_ACEITOS = ['application/pdf', 'image/png', 'image/jpeg'];

  type CampoAnexo = 'descricao' | 'tipoDocumento' | 'dataDocumento' | 'arquivo';

  function hojeIso(): string {
    const agora = new Date();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');
    return `${agora.getFullYear()}-${mes}-${dia}`;
  }

  /**
   * Documentos anexados à sala/ambiente. A listagem traz o link temporário de cada arquivo, que
   * é aberto por um link comum — buscar a URL no clique e só então abrir seria bloqueado pelo
   * navegador como pop-up.
   *
   * Permissão: a da tela-mãe (inject). Anexar é `incluir`; excluir é `excluir`; a lista e o link
   * do arquivo são `visualizar`.
   */
  export default defineComponent({
    name: 'AnexosAmbiente',
    components: { ConsulteGrid, DropDownConsulte },
    inject: { permissao: { from: PERMISSAO_TELA_INJECT_KEY, default: permissaoTelaLiberada } },
    props: {
      salaId: { type: Number, required: true },
      tiposDocumento: {
        type: Array as () => { valor: number; texto: string }[],
        default: () => [],
      },
    },
    data() {
      return {
        idBase: novoId('sala-anexo'),
        ocupado: false,
        anuncio: '',
        erroGeral: '',
        hoje: hojeIso(),
        arquivo: null as File | null,
        sequencia: 0,
        novo: { descricao: '', tipoDocumento: null as number | null, dataDocumento: '' },
        erros: {} as Partial<Record<CampoAnexo, string>>,
        dataSource: new DataSourceLocal<AnexoSalaModel>(),
        colunas: (
          [
            ['Descrição', 'descricao', true],
            ['Tipo', 'tipoDocumentoDescricao', true],
            ['Data de criação', 'dataDocumento', false],
            ['Arquivo', 'nomeArquivo', true],
            ['Tamanho', 'tamanho', false],
            ['Anexado por', 'nomeUsuario', true],
            ['Ações', 'acoes', false],
          ] as [string, string, boolean][]
        ).map(([text, datafield, filterable]) =>
          Object.assign(new ConsulteGridColumnsModel(), {
            text,
            datafield,
            filterable,
            sortable: datafield !== 'acoes',
            filtertype: 'text',
          }),
        ),
      };
    },
    watch: {
      salaId: {
        handler() {
          this.consultar();
        },
        immediate: true,
      },
    },
    beforeUnmount() {
      ++this.sequencia;
    },
    methods: {
      async consultar() {
        const sequencia = ++this.sequencia;
        this.erroGeral = '';
        try {
          const linhas = await salaService.listarAnexos(this.salaId);
          if (sequencia === this.sequencia) this.dataSource.definirItens(linhas);
        } catch {
          if (sequencia !== this.sequencia) return;
          this.dataSource.definirItens([]);
          this.erroGeral = 'Não foi possível carregar os documentos do ambiente.';
        }
      },

      selecionarArquivo(evento: Event) {
        this.arquivo = (evento.target as HTMLInputElement).files?.[0] ?? null;
        delete this.erros.arquivo;
      },

      validar(): boolean {
        const erros: Partial<Record<CampoAnexo, string>> = {};
        const descricao = this.novo.descricao.trim();

        if (!descricao) erros.descricao = 'Informe a descrição do documento.';
        if (!this.novo.tipoDocumento) erros.tipoDocumento = 'Selecione o tipo do documento.';

        if (!this.novo.dataDocumento) erros.dataDocumento = 'Informe a data de criação do documento.';
        else if (this.novo.dataDocumento > this.hoje)
          erros.dataDocumento = 'A data de criação do documento não pode ser futura.';

        if (!this.arquivo) erros.arquivo = 'Selecione o arquivo do documento.';
        else if (this.arquivo.size > TAMANHO_MAXIMO) erros.arquivo = 'O arquivo pode ter no máximo 10 MB.';
        else if (this.arquivo.type && !TIPOS_ACEITOS.includes(this.arquivo.type))
          erros.arquivo = 'O arquivo precisa ser PDF, JPG ou PNG.';

        this.erros = erros;

        const primeiro = (['descricao', 'tipoDocumento', 'dataDocumento', 'arquivo'] as CampoAnexo[]).find(
          (c) => erros[c],
        );
        if (primeiro) {
          const sufixo = { descricao: 'descricao', tipoDocumento: 'tipo', dataDocumento: 'data', arquivo: 'arquivo' }[
            primeiro
          ];
          this.$nextTick(() => document.getElementById(`${this.idBase}-${sufixo}`)?.focus());
          this.anuncio = 'O documento não foi anexado: corrija os campos indicados.';
          return false;
        }
        return true;
      },

      async enviar() {
        const permissao = this.permissao as PermissaoTela;
        if (!permissao.incluir || this.ocupado || !this.validar() || !this.arquivo) return;

        this.ocupado = true;
        this.erroGeral = '';
        // Cópia congelada: o formulário é limpo logo abaixo e o axios serializa depois.
        const dados = {
          descricao: this.novo.descricao.trim(),
          tipoDocumento: Number(this.novo.tipoDocumento),
          dataDocumento: this.novo.dataDocumento,
        };
        try {
          await salaService.enviarAnexo(this.salaId, dados, this.arquivo);
          this.novo = { descricao: '', tipoDocumento: null, dataDocumento: '' };
          this.arquivo = null;
          const campo = this.$refs.campoArquivo as HTMLInputElement | undefined;
          if (campo) campo.value = '';
          await this.consultar();
          this.anuncio = 'Documento anexado.';
          toastService.alertaSucesso('Documento anexado com sucesso!');
        } catch {
          // A mensagem do servidor (400) já sai pelo interceptor global.
          this.anuncio = 'O documento não foi anexado.';
        } finally {
          this.ocupado = false;
        }
      },

      async excluir(linha: AnexoSalaModel) {
        const permissao = this.permissao as PermissaoTela;
        if (!permissao.excluir || this.ocupado) return;
        const confirmado = await dialogoService.confirmar({
          mensagem: `Excluir o documento "${linha.descricao}"? O arquivo também será apagado.`,
          textoConfirmar: 'Excluir',
          perigo: true,
        });
        if (!confirmado) return;

        this.ocupado = true;
        try {
          await salaService.removerAnexo(this.salaId, linha.codigo);
          await this.consultar();
          this.anuncio = 'Documento excluído.';
          toastService.alertaSucesso('Documento excluído com sucesso!');
        } catch {
          this.anuncio = 'O documento não foi excluído.';
        } finally {
          this.ocupado = false;
        }
      },

      formatarData(valor?: string): string {
        if (!valor) return '';
        const [ano, mes, dia] = valor.split('T')[0].split('-');
        return dia && mes && ano ? `${dia}/${mes}/${ano}` : valor;
      },

      formatarTamanho(bytes: number): string {
        if (!bytes) return '';
        if (bytes < 1024 * 1024)
          return `${(bytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
        return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
      },
    },
  });
</script>
